import { get } from 'svelte/store';
import { app } from '$lib/stores/app/store';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, toUnderscore, type Locale } from './locale';

/**
 * Normalizes and clamps a locale to an authored runtime bundle.
 * @param {Locale | null | undefined} locale - Candidate locale.
 * @returns {Locale | null} Supported locale, or null when unsupported.
 */
function clampToSupported(locale: Locale | null | undefined): Locale | null {
	if (!locale) return null;
	const normalized = toUnderscore(locale);
	if (SUPPORTED_LOCALES.includes(normalized)) return normalized;
	if (normalized.startsWith('es')) return 'es_MX';
	return null;
}

/**
 * Resolves the display-only locale. Experiment bucketing and API payloads
 * still send the raw `localeCode` BCP-47 value; this function only feeds
 * `activeLocale` / `$l10n()`.
 *
 * Precedence: `localeOverride` > `savedDisplayLocale` > `localeCode` >
 * `navigator.language` > `en_US`. Any `es*` value clamps to `es_MX`.
 * @returns {Locale} Active display locale.
 */
export function resolveDisplayLocale(): Locale {
	const meta = get(app).meta;
	if (!meta.l10nEnabled) return DEFAULT_LOCALE;

	const override = clampToSupported(meta.localeOverride);
	if (override) return override;

	const saved = clampToSupported(meta.savedDisplayLocale);
	if (saved) return saved;

	const fromLocaleCode = clampToSupported(meta.localeCode);
	if (fromLocaleCode) return fromLocaleCode;

	if (typeof navigator !== 'undefined') {
		const browserLocale = clampToSupported(navigator.language);
		if (browserLocale) return browserLocale;
	}

	return DEFAULT_LOCALE;
}
